import React, { useMemo, useState } from 'react';
import { Alert, Avatar, Box, Container, Paper, Snackbar, Typography } from '@mui/material';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import Navbar from '../../Navbar';
import BarraFiltros from './components/BarraFiltros';
import TablaPosnetsChoferes from './components/TablaPosnetsChoferes';
import ModalAsignarPosnet from './components/ModalAsignarPosnet';
import ModalHistorial from './components/ModalHistorial';
import usePosnetsChoferes from './hooks/usePosnetsChoferes';
import { empresasConfig } from '../sucursalesMercadoLibre/config';

const PosnetsChoferes = () => {
  const [empresa, setEmpresa] = useState('hipergas');
  const [sucursal, setSucursal] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [choferAsignar, setChoferAsignar] = useState(null);
  const [choferHistorial, setChoferHistorial] = useState(null);
  const [snack, setSnack] = useState({ open: false, mensaje: '', severidad: 'success' });

  const { choferes, terminales, loading, error, errorTerminales, refetch } = usePosnetsChoferes(
    empresasConfig[empresa]
  );

  const terminalesPorId = useMemo(
    () => Object.fromEntries(terminales.map((t) => [t.id, t])),
    [terminales]
  );

  const terminalesPDV = useMemo(
    () => terminales.filter((t) => t.operating_mode === 'PDV'),
    [terminales]
  );

  const choferPorPosnet = useMemo(
    () => Object.fromEntries(choferes.filter((c) => c.posnet).map((c) => [c.posnet, c])),
    [choferes]
  );

  const sucursales = useMemo(() => {
    const map = new Map();
    choferes.forEach((c) => {
      if (c.id_sucursal && !map.has(c.id_sucursal)) map.set(c.id_sucursal, { id: c.id_sucursal, nombre: c.sucursal });
    });
    return [...map.values()].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
  }, [choferes]);

  const filtrados = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();
    return choferes.filter((c) => {
      if (sucursal && c.id_sucursal !== sucursal) return false;
      if (!texto) return true;
      return (
        (c.nombre_apellido || '').toLowerCase().includes(texto) ||
        (c.posnet || '').toLowerCase().includes(texto)
      );
    });
  }, [choferes, sucursal, busqueda]);

  const pagina = filtrados.slice(page * pageSize, page * pageSize + pageSize);

  const handleEmpresaChange = (nuevaEmpresa) => {
    setEmpresa(nuevaEmpresa);
    setSucursal('');
    setPage(0);
  };

  const handleSucursalChange = (nuevaSucursal) => {
    setSucursal(nuevaSucursal);
    setPage(0);
  };

  const handleBusquedaChange = (texto) => {
    setBusqueda(texto);
    setPage(0);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(0);
  };

  const handleAsignarSuccess = (mensaje) => {
    setChoferAsignar(null);
    setSnack({ open: true, mensaje, severidad: 'success' });
    refetch();
  };

  const handleCloseSnack = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnack((prev) => ({ ...prev, open: false }));
  };

  return (
    <>
      <Navbar />
      <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
        <Paper
          variant="outlined"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 2,
            mb: 2,
            borderRadius: 2,
          }}
        >
          <Avatar sx={{ bgcolor: 'primary.main', width: 44, height: 44 }}>
            <PointOfSaleIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              Posnets de choferes
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Asigná, cambiá o quitá las terminales Point de los choferes de envasado
            </Typography>
          </Box>
        </Paper>

        <BarraFiltros
          empresa={empresa}
          onEmpresaChange={handleEmpresaChange}
          sucursales={sucursales}
          sucursal={sucursal}
          onSucursalChange={handleSucursalChange}
          busqueda={busqueda}
          onBusquedaChange={handleBusquedaChange}
          onRefresh={refetch}
          loading={loading}
          total={filtrados.length}
        />

        {errorTerminales && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            No se pudieron cargar las terminales de Mercado Pago: {errorTerminales}
          </Alert>
        )}

        <TablaPosnetsChoferes
          data={pagina}
          total={filtrados.length}
          loading={loading}
          error={error}
          terminalesPorId={terminalesPorId}
          terminalesCargadas={!loading && !errorTerminales}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
          onCambiar={setChoferAsignar}
          onHistorial={setChoferHistorial}
        />

        <ModalAsignarPosnet
          open={!!choferAsignar}
          chofer={choferAsignar}
          terminalesPDV={terminalesPDV}
          choferPorPosnet={choferPorPosnet}
          onClose={() => setChoferAsignar(null)}
          onSuccess={handleAsignarSuccess}
        />

        <ModalHistorial
          open={!!choferHistorial}
          chofer={choferHistorial}
          onClose={() => setChoferHistorial(null)}
        />

        <Snackbar
          open={snack.open}
          autoHideDuration={2500}
          onClose={handleCloseSnack}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnack} severity={snack.severidad} variant="filled" sx={{ width: '100%' }}>
            {snack.mensaje}
          </Alert>
        </Snackbar>
      </Container>
    </>
  );
};

export default PosnetsChoferes;
